import type { AuthResponse, ProblemDetails, UserDto } from './types';
import { STORAGE_KEYS, storage } from '@/lib/storage';

/**
 * The API: VITE_API_URL when set (production builds), otherwise port 5080 on the host this page came from — so a phone that opened the
 * app at the computer's network address (start.command --lan) talks to the API on that computer too. An empty VITE_API_URL means the
 * API is on the same address as the web app (deploy/: Caddy sends /api, /ingest, /hubs… to it).
 */
export const API_URL: string = (
  import.meta.env.VITE_API_URL ?? (typeof window !== 'undefined' && window.location?.hostname
    ? `${window.location.protocol}//${window.location.hostname}:5080`
    : 'http://localhost:5080')
).replace(/\/+$/, '');

/** The API's full address, for what runs elsewhere (the recorder in a customer's app, the Android SDK, the QR link). */
export const PUBLIC_API_URL: string = API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// ----------------------------------------------------------------------------- errors

/** A failed API call. Carries the parsed RFC 7807 ProblemDetails. */
export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  /** Field -> messages, keys normalised to react-hook-form paths (`steps.0.action`). */
  readonly fieldErrors: Record<string, string[]>;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, fallbackTitle?: string) {
    const title = problem?.title || fallbackTitle || `HTTP ${status}`;
    super(problem?.detail || title);
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.detail = problem?.detail;
    this.problem = problem;
    this.fieldErrors = normaliseFieldErrors(problem?.errors);
  }

  get isValidation(): boolean {
    return this.status === 400 && Object.keys(this.fieldErrors).length > 0;
  }
}

/** Network failure (server down, CORS, offline). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * .NET emits keys like `Title`, `Steps[0].Action`, `$.steps[1].expectedResult`, or `request` (body-level).
 * Convert them to react-hook-form paths: `title`, `steps.0.action`, `steps.1.expectedResult`.
 */
export function toFieldPath(key: string): string {
  return key
    .replace(/^\$\.?/, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
    .map((seg) => (/^\d+$/.test(seg) ? seg : seg.charAt(0).toLowerCase() + seg.slice(1)))
    .join('.');
}

function normaliseFieldErrors(errors: Record<string, string[]> | undefined): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!errors) return out;
  for (const [key, messages] of Object.entries(errors)) {
    const path = toFieldPath(key) || 'root';
    out[path] = [...(out[path] ?? []), ...(Array.isArray(messages) ? messages : [String(messages)])];
  }
  return out;
}

type SetError = (name: never, error: { type: string; message: string }, options?: { shouldFocus: boolean }) => void;

/**
 * Push server validation errors into a react-hook-form instance.
 * Returns true when at least one field error was applied (caller can skip a toast).
 * Unknown fields go to `root.server` so the form can show them in a banner.
 */
export function applyFieldErrors(
  error: unknown,
  setError: SetError,
  knownFields?: readonly string[],
): boolean {
  if (!(error instanceof ApiError) || error.status !== 400) return false;
  const entries = Object.entries(error.fieldErrors);
  if (entries.length === 0) return false;
  let first = true;
  const unknown: string[] = [];
  for (const [path, messages] of entries) {
    const top = path.split('.')[0];
    if (knownFields && !knownFields.includes(top)) {
      unknown.push(...messages);
      continue;
    }
    (setError as (n: string, e: { type: string; message: string }, o?: { shouldFocus: boolean }) => void)(
      path,
      { type: 'server', message: messages.join(' ') },
      { shouldFocus: first },
    );
    first = false;
  }
  if (unknown.length) {
    (setError as (n: string, e: { type: string; message: string }) => void)('root.server', {
      type: 'server',
      message: unknown.join(' '),
    });
  }
  return true;
}

// ----------------------------------------------------------------------------- session tokens

interface TokenState {
  accessToken: string | null;
  accessTokenExpiresAt: number | null; // epoch ms
}

const tokens: TokenState = { accessToken: null, accessTokenExpiresAt: null };

type SessionListener = (event: { type: 'tokens'; user?: UserDto } | { type: 'logout' }) => void;
const listeners = new Set<SessionListener>();

export function onSessionEvent(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function emit(event: Parameters<SessionListener>[0]) {
  listeners.forEach((l) => l(event));
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  if (!tokens.accessTokenExpiresAt) return;
  const delay = tokens.accessTokenExpiresAt - Date.now() - 60_000;
  // Keep the in-memory token fresh so `?access_token=` URLs and SignalR keep working.
  refreshTimer = setTimeout(() => {
    void refreshSession().catch(() => undefined);
  }, Math.max(5_000, delay));
}

export function setSession(auth: AuthResponse): void {
  tokens.accessToken = auth.accessToken;
  const exp = Date.parse(auth.accessTokenExpiresAt);
  tokens.accessTokenExpiresAt = Number.isFinite(exp) ? exp : Date.now() + 14 * 60_000;
  storage.set(STORAGE_KEYS.refreshToken, auth.refreshToken);
  scheduleProactiveRefresh();
  emit({ type: 'tokens', user: auth.user });
}

export function clearSession(): void {
  tokens.accessToken = null;
  tokens.accessTokenExpiresAt = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  storage.remove(STORAGE_KEYS.refreshToken);
}

export function getAccessToken(): string | null {
  return tokens.accessToken;
}

export function getRefreshToken(): string | null {
  return storage.get(STORAGE_KEYS.refreshToken);
}

export function hasStoredSession(): boolean {
  return !!getRefreshToken();
}

let refreshInFlight: Promise<boolean> | null = null;

const DEVICE_KEY = 'tp.device';
let memoryDevice: string | null = null;

/**
 * A random id for this browser (kept in localStorage), sent with every request as `X-TP-Device`. It lets platform admins see
 * accounts opened from the same browser; it carries nothing about the person.
 */
export function deviceId(): string {
  if (memoryDevice) return memoryDevice;
  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    if (stored && /^[A-Za-z0-9_-]{8,100}$/.test(stored)) return (memoryDevice = stored);
  } catch {
    /* storage blocked: keep the id for this page only */
  }
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  try {
    localStorage.setItem(DEVICE_KEY, id);
  } catch {
    /* ignore */
  }
  return (memoryDevice = id);
}

async function doRefresh(): Promise<boolean> {
  // Re-read inside the (optional) cross-tab lock: another tab may have rotated the token already.
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-TP-Device': deviceId() },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Network failure: keep the stored refresh token, the next call may succeed.
    return false;
  }
  if (!res.ok) {
    // Busy or failing server: keep the refresh token, a later call may succeed. A refused token ends the session — unless another tab
    // replaced it in the meantime.
    if (res.status === 429 || res.status >= 500) return false;
    if (getRefreshToken() === refreshToken) clearSession();
    return false;
  }
  const auth = (await res.json()) as AuthResponse;
  setSession(auth);
  return true;
}

/**
 * Single-flight refresh: concurrent 401s share one `/api/auth/refresh` call.
 * Uses the Web Locks API when available so several tabs don't race the rotating refresh token.
 */
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
    const run = locks?.request ? locks.request('tp-auth-refresh', () => doRefresh()) : doRefresh();
    refreshInFlight = Promise.resolve(run).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Returns a token that is valid for at least ~30 s, refreshing when needed (SignalR, downloads). */
export async function getFreshAccessToken(): Promise<string | null> {
  const soon = Date.now() + 30_000;
  if (tokens.accessToken && tokens.accessTokenExpiresAt && tokens.accessTokenExpiresAt > soon) {
    return tokens.accessToken;
  }
  const ok = await refreshSession();
  return ok ? tokens.accessToken : null;
}

function handleUnauthenticated() {
  clearSession();
  emit({ type: 'logout' });
}

// ----------------------------------------------------------------------------- fetch wrapper

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** false for public endpoints: no Authorization header, no refresh-on-401. */
  auth?: boolean;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = /^https?:\/\//.test(path) ? path : `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  if (!qs) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${qs}`;
}

async function parseProblem(res: Response): Promise<ProblemDetails | null> {
  const text = await res.text().catch(() => '');
  if (!text) return null;
  try {
    const json = JSON.parse(text) as Partial<ProblemDetails>;
    if (json && typeof json === 'object') {
      return {
        type: json.type,
        title: json.title ?? res.statusText,
        status: json.status ?? res.status,
        detail: json.detail,
        errors: json.errors,
        // Coded refusals (a lapsed membership…) say what they are about.
        ...(typeof json.code === 'string' ? { code: json.code, audience: json.audience, organizationId: json.organizationId } : {}),
      };
    }
  } catch {
    /* not JSON */
  }
  return { title: res.statusText || `HTTP ${res.status}`, status: res.status, detail: text.slice(0, 500) };
}

async function send(url: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-TP-Device': deviceId() };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (opts.auth !== false && tokens.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  try {
    return await fetch(url, { method: opts.method ?? 'GET', headers, body, signal: opts.signal });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new NetworkError(e);
  }
}

/** Low-level request returning the raw Response (after auth/refresh handling and error mapping). */
export async function apiRaw(path: string, opts: RequestOptions = {}): Promise<Response> {
  const url = buildUrl(path, opts.query);
  const authed = opts.auth !== false;

  // No access token yet but a refresh token exists (e.g. page reload): refresh first.
  if (authed && !tokens.accessToken && getRefreshToken()) {
    await refreshSession();
  }

  let res = await send(url, opts);
  if (res.status === 401 && authed) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await send(url, opts);
    }
    // Signed out only when the session is really over (the refresh token was refused); a busy server just fails this request.
    if (res.status === 401 && (refreshed || !getRefreshToken())) {
      handleUnauthenticated();
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, await parseProblem(res));
  }
  return res;
}

/** JSON request. Returns `undefined` for 204 / empty bodies. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await apiRaw(path, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Uploads a file as the raw request body (large videos stream to the server without multipart buffering), reporting progress
 * from 0 to 1. Refreshes the session once on 401, like {@link apiRaw}.
 */
export async function uploadFile<T>(
  path: string, file: Blob,
  { query, onProgress, signal, auth = true }: { query?: Record<string, QueryValue>; onProgress?: (fraction: number) => void; signal?: AbortSignal; auth?: boolean } = {},
): Promise<T> {
  const url = buildUrl(path, query);
  const attempt = async (): Promise<{ status: number; text: string; statusText: string }> => {
    const token = auth ? await getFreshAccessToken() : null;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText, statusText: xhr.statusText });
      xhr.onerror = () => reject(new NetworkError(new Error('upload failed')));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(file);
    });
  };
  let res = await attempt();
  const refreshed = auth && res.status === 401 && (await refreshSession());
  if (refreshed) res = await attempt();
  if (auth && res.status === 401 && (refreshed || !getRefreshToken())) handleUnauthenticated();
  if (res.status < 200 || res.status >= 300) {
    throw new ApiError(res.status, await parseProblem(new Response(res.text || null, { status: res.status, statusText: res.statusText })));
  }
  onProgress?.(1);
  return (res.text ? JSON.parse(res.text) : undefined) as T;
}

/**
 * Absolute URL for `<img>` / `<a>` / `<video>` pointing at a protected server resource
 * (attachments are returned as `/api/attachments/{id}/content`). Appends `?access_token=`.
 */
export function authedUrl(relative: string | null | undefined): string {
  if (!relative) return '';
  const token = tokens.accessToken;
  const abs = buildUrl(relative);
  if (!token) return abs;
  // With the API on the web app's own address (production), `abs` is a path: resolve it against the page.
  const u = new URL(abs, window.location.origin);
  u.searchParams.set('access_token', token);
  return u.toString();
}

/** Test helper: reset in-memory state between tests. */
export function __resetClientForTests(): void {
  clearSession();
  refreshInFlight = null;
  listeners.clear();
}
